import {
    App,
    Col,
    Row,
    TableColumnsType,
    Tooltip,
    Button as ButtonAnt,
  } from 'antd';
  import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
  import { Table } from '@/components/tables/table';
  import { useAppDispatch, useAppSelector } from '@/redux/hooks';
  import {
    deleteBanner,
    resetMessages,
    watchBannerLoading,
    watchBannerMessage,
    watchBannerPagination,
    watchListBanner
  } from '@/redux/slices/banner.slice';
  import { useEffect } from 'react';
  import { setMessages } from '@/redux/slices/app.slice';
  import { useQuery } from '@/hooks/query.hook';
  import { PageURLs } from '@/utils/navigate';
  import { PermissionMode } from '@/enums/mode.enum';
  import { CustomMessage } from '../message';
  import { formatDateToDDMMYYYY, tableScrollY } from '@/utils/common';
  
  type PageChangeParams = {
    page?: number;
    pageSize?: number;
    filter?: FilterBanner;
  };
  
  type BannerSliderTableProps = {
    handleSearch: (values: FilterBanner) => void;
    handlePageChange: ({ page, pageSize, filter }: PageChangeParams) => void;
    filter: FilterBanner;
  };
  
  export const ChatGPTManagementTable = ({
    handleSearch,
    handlePageChange,
    filter
  }: BannerSliderTableProps) => {
    const dispatch = useAppDispatch();
    const { modal } = App.useApp();
    const listBanner = useAppSelector(watchListBanner);
    const messageInfo = useAppSelector(watchBannerMessage);
    const pagination = useAppSelector(watchBannerPagination);
    const loading = useAppSelector(watchBannerLoading);
    const { push } = useQuery();
  
    const handleReset = () => {
      handlePageChange({});
    };
  
    const handleDeleteBanner = async (bannerId: number) => {
      await dispatch(deleteBanner(bannerId));
      const remainingRecords = listBanner.length - 1;
      if (remainingRecords === 0 && pagination.curPage > 1) {
        handlePageChange({
          page: pagination.curPage - 1,
          pageSize: pagination.limitPage,
          filter
        });
      } else {
        handlePageChange({
          page: pagination.curPage,
          pageSize: pagination.limitPage,
          filter
        });
      }
    };
  
    const confirmDeleteAdmin = (bannerId: number) => {
      modal.confirm({
        title: 'Bạn có chắc chắn xoá Banner này không?',
        content: <em>Sau khi xoá sẽ không thể hoàn tác</em>,
        okText: 'Xác nhận',
        width: 450,
        cancelText: 'Hủy',
        onOk: () => handleDeleteBanner(bannerId)
      });
    };
  
    const handleEditBanner = (bannerId: string) => {
      push(
        `${PageURLs.ofHomeBannerSlider()}?mode=${PermissionMode.EDIT}&bannerId=${bannerId}`
      );
    };
  
    const columns: TableColumnsType<Banner> = [
      {
        title: 'STT',
        render: (_, __, index) => index + 1
      },
      {
        title: 'Câu hỏi',
        dataIndex: 'name',
        key: 'name',
        width: 400
      },
      {
        title: 'Trạng thái',
        dataIndex: ['option', 'name'],
        render: (__, record) => {
          let messageType: MessageType;
          switch (record?.option?.name) {
            case 'Website cho pc':
              messageType = 'website';
              break;
            case 'Website cho Mobile':
              messageType = 'website-mobile';
              break;
            case 'App':
              messageType = 'app';
              break;
            default:
              messageType = '';
          }
          return (
            record.option && (
              <CustomMessage hasBackground type={messageType}>
                {record?.option?.name}
              </CustomMessage>
            )
          );
        }
      },
      {
        title: 'Người tạo',
        dataIndex: 'createdBy',
        key: 'createdBy',
        width: 150
      },
      {
        title: 'Ngày tạo',
        dataIndex: 'createdDate',
        key: 'createdDate',
        width: 150,
        render: (record) => (record ? formatDateToDDMMYYYY(record) : '')
      },
      {
        title: 'Người cập nhật',
        dataIndex: 'modifiedBy',
        key: 'modifiedBy',
        width: 150
      },
      {
        title: 'Ngày cập nhật',
        dataIndex: 'modifiedDate',
        key: 'modifiedDate',
        width: 150,
        render: (record) => (record ? formatDateToDDMMYYYY(record) : '')
      },
      {
        title: 'Thao tác',
        key: 'action',
        fixed: 'right',
        width: 104,
        render: (record) => (
          <Row gutter={[8, 10]}>
            <Col>
              <Tooltip title='Cập nhật'>
                <ButtonAnt
                  onClick={() => handleEditBanner(record.id)}
                  type='primary'
                  icon={<EditOutlined />}
                />
              </Tooltip>
            </Col>
            <Col>
              <Tooltip title='Xoá'>
                <ButtonAnt
                  onClick={() => confirmDeleteAdmin(record.id)}
                  type='primary'
                  danger
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Col>
          </Row>
        )
      }
    ];
  
    useEffect(() => {
      handlePageChange({});
    }, [handlePageChange]);
  
    useEffect(() => {
      if (messageInfo) {
        dispatch(
          setMessages([
            {
              type: messageInfo.type,
              message: messageInfo.message,
              description: messageInfo.description
            }
          ])
        );
        dispatch(resetMessages());
      }
    }, [dispatch, messageInfo]);
    return (
      <Table<Banner>
        rowKey={(record) => record.id}
        columns={columns}
        dataSource={listBanner}
        placeholder='Nhập từ khóa tìm kiếm câu hỏi'
        loading={loading}
        totalResult={pagination.totalRows}
        scrollY={tableScrollY(pagination.limitPage)}
        pagination={{
          showQuickJumper: true,
          current: pagination.curPage,
          pageSize: pagination.limitPage,
          total: pagination.totalRows,
          onChange: (page, pageSize) => {
            handlePageChange({ page, pageSize });
          }
        }}
        onFilter={handleSearch}
        onReset={handleReset}
      />
    );
  };
  